// Put-Away (Plassering) related types - mirroring picking types structure
export interface StcIntRelOrderDto {
  id: number;
  companyNo: string;
  warehouseID: string;
  stcOrderNo: number;
  transDt: string;
  orderStatus: number;
  transType: number;
  operatorID?: string;
  operatorGroupID?: string;
  orderPriority: number;
  description1?: string;
  lockStatus: number;
  lockedBy?: string;
  sessionID?: string;
  externalSystemID?: string;
  created: string;
  lastUpdate?: string;
  createdBy?: string;
  updatedBy?: string;
  statusText: string;
  transTypeText: string;
  lineCount: number;
  isLocked: boolean;
}

export interface StcIntRelOrderLineDto {
  id: number;
  companyNo: string;
  warehouseID: string;
  stcOrderNo: number;
  stcLineNo: number;
  itemNo: string;
  quantity: number;
  fromBinCode?: string;
  toBinCode?: string;
  grShipmentNo?: number;
  stcBoxNo?: string;
  lineStatus: number;
  movedBy?: string;
  itemGrp1?: string;
  itemGrp2?: string;
  itemGrp3?: string;
  description1?: string;
  resourceNo?: number;
  stcZoneID?: string;
  transType: number;
  clientID?: string;
  quantityMoved?: number;
  itemBarCode?: string;
  externalSystemID?: string;
  externalSystemStatus?: string;
  resID?: number;
  binGrpNo?: number;
  sorting?: number;
  relID?: number;
  created: string;
  lastUpdate?: string;
  createdBy?: string;
  updatedBy?: string;
  statusText: string;
  transTypeText: string;
  remainingQuantity: number;
  isCompleted: boolean;
}

export interface StcIntRelOrderDetailsDto {
  header: StcIntRelOrderDto;
  lines: StcIntRelOrderLineDto[];
  totalLines: number;
  completedLines: number;
  totalQuantity: number;
  movedQuantity: number;
  completionPercentage: number;
}

export interface UpdateQuantityMovedRequest {
  quantityMoved: number;
  movedBy?: string;
}

export interface UpdateStcIntRelOrderStatusRequest {
  status: number;
}

export interface LockStcIntRelOrderRequest {
  operatorID?: string;
}

// Put-Away status definitions (similar to picking statuses)
export const PutAwayStatuses = {
  NY: 0,
  TILDELT: 10,
  UNDER_ARBEID: 20,
  PAUSET: 30,
  FERDIG: 100,
  AVBRUTT: 110,
} as const;

export type PutAwayStatus = typeof PutAwayStatuses[keyof typeof PutAwayStatuses];

// Put-Away transaction types
export const PutAwayTransTypes = {
  INTERN_FLYTTING: 0,
  PLUKK_TIL_PAKKING: 1,
  OMFLYTTING: 2,
  JUSTERING: 3,
} as const;

export type PutAwayTransType = typeof PutAwayTransTypes[keyof typeof PutAwayTransTypes];

// Legacy put-away types (keep for backwards compatibility)
export interface PutAwayReservationDto {
  binCode: string;
  stcZoneID: string;
  resourceNo: number;
  stcBinGrp: number;
  resQuantity: number;
  quantity: number;
  pickableBinYesNo: boolean;
  dummy: boolean;
}

export interface CreatePutAwayRequest {
  companyNo: string;
  warehouseID: string;
  itemNo: string;
  binGrpNo: number;
  pickRuleNo: number;
  grShipmentNo: number;
  putAwayToPickableBinYesNo: boolean;
  quantity: number;
}

// Location suggestion source enum - matching backend LocationSuggestionSource
export enum LocationSuggestionSource {
  None = 0,
  ErpPOrderLine = 1,
  ItemStdBinCode = 2,
  Calculated = 3,
  Placeholder = 4
}

export interface CreatePutAwayResponse {
  success: boolean;
  reservations: PutAwayReservationDto[];
  errorMessage?: string;
  isStandardMode: boolean;
  suggestedLocationSource: LocationSuggestionSource;
}

// New completion endpoint types (aligned with backend DTOs in CompletePutAwayDtos.cs)
export interface CompletePutAwayLineRequest {
  warehouseID: string;
  toBinCode: string;
  quantity: number;
}

export interface ItemBinsUpdateInfo {
  binCode: string;
  itemNo: string;
  quantityChange: number;
}

export interface CompletePutAwayLineResponse {
  success: boolean;
  errorMessage?: string;
  message?: string;
  updatedItemBins: ItemBinsUpdateInfo[];
  /** Mengde plassert i denne operasjonen */
  placedQuantity: number;
  /** Resterende mengde (undefined hvis full plassering) */
  remainingQuantity?: number;
  /** StcLineNo for ny restlinje ved split-plassering (undefined hvis full) */
  newLineStcLineNo?: number;
  /** true hvis hele mengden er plassert */
  isFullyPlaced: boolean;
}

export interface CompletePutAwayBulkRequest {
  warehouseID: string;
  toBinCode: string;
}

export interface CompletedLineInfo {
  stcLineNo: number;
  itemNo: string;
  quantity: number;
  fromBinCode: string;
  toBinCode: string;
}

export interface CompletePutAwayBulkResponse {
  success: boolean;
  errorMessage?: string;
  message?: string;
  completedCount: number;
  completedLines: CompletedLineInfo[];
}
